import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import Layout from './components/Layout/Layout';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));

// Loading fallback
const LoadingFallback = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
  </div>
);

// Error fallback
const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error, resetErrorBoundary: () => void }) => {
  const isMissingEnvVars = error.message.includes('Missing Supabase environment variables');

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-error-600 dark:text-error-400 mb-2">
            {isMissingEnvVars ? 'Configuration Error' : 'Something went wrong'}
          </h2>
          <p className="text-slate-600 dark:text-slate-400">{error.message}</p>
        </div>

        {isMissingEnvVars && (
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-6 mb-6 text-left">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
              To fix this issue:
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600 dark:text-slate-400">
              <li>Set the following environment variables in your hosting platform:</li>
            </ol>
            <div className="mt-3 bg-slate-900 dark:bg-slate-950 rounded p-4 overflow-x-auto">
              <code className="text-green-400 text-xs">
                VITE_SUPABASE_URL=your_supabase_url<br/>
                VITE_SUPABASE_ANON_KEY=your_supabase_anon_key
              </code>
            </div>
            <div className="mt-4 text-sm text-slate-600 dark:text-slate-400">
              <p className="mb-2"><strong>For Netlify:</strong> Go to Site settings → Environment variables</p>
              <p className="mb-2"><strong>For Vercel:</strong> Go to Project Settings → Environment Variables</p>
              <p><strong>For other platforms:</strong> Check their documentation for setting environment variables</p>
            </div>
          </div>
        )}

        <button
          onClick={resetErrorBoundary}
          className="btn-primary w-full"
        >
          Try again
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AuthProvider>
        <Router>
          <Layout>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Layout>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;